import type { RelatedPlace } from "~/lib/api";
import { cn } from "~/lib/utils";
import { Icon, type IconType } from "~/components/ui/icons/icon";

interface RelatedPlaceItemProps {
  place: RelatedPlace;
  icon: IconType;
  onRemove?: () => void;
  onSelect?: () => void;
  disabled?: boolean;
}

export function RelatedPlaceItem({ place, icon, onRemove, onSelect, disabled }: RelatedPlaceItemProps) {
  return (
    <div className="relative shrink-0">
      <Tile place={place} icon={icon} onClick={onSelect} />
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${place.name}`}
          className={cn(
            "absolute -right-1.5 -top-1.5 flex items-center justify-center rounded-full p-0.5",
            "bg-background text-muted-foreground",
            "transition-colors hover:bg-muted",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            { "cursor-not-allowed opacity-50": disabled },
          )}
        >
          <Icon i="delete-image" className="size-5" />
        </button>
      )}
    </div>
  );
}

interface TileProps {
  place: RelatedPlace;
  icon: IconType;
  onClick?: () => void;
}

function Tile({ place, icon, onClick }: TileProps) {
  const thumbnail = place.images[0]?.thumb;
  const interactive = !!onClick;
  const Tag = interactive ? "button" : "div";

  const interactiveClasses = {
    "cursor-pointer transition-transform hover:scale-105": interactive,
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring": interactive,
  };

  if (thumbnail) {
    return (
      <Tag
        type={interactive ? "button" : undefined}
        onClick={onClick}
        aria-label={interactive ? `View ${place.name}` : undefined}
        className={cn("relative size-18 overflow-hidden rounded-lg", interactiveClasses)}
      >
        <img src={thumbnail} alt={place.name} className="size-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 bg-black/50 px-1 py-0.5">
          <span className="block max-w-16 truncate text-xs text-white">{place.name}</span>
        </div>
      </Tag>
    );
  }

  return (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={onClick}
      aria-label={interactive ? `View ${place.name}` : undefined}
      className={cn(
        "relative flex size-18 flex-col items-center justify-center gap-1 rounded-lg bg-muted/50",
        interactiveClasses,
      )}
    >
      <Icon i={icon} className="size-6 text-muted-foreground" />
      <span className="max-w-16 truncate text-xs text-muted-foreground">{place.name}</span>
    </Tag>
  );
}
